import React, { useState } from 'react'
import styled from 'styled-components'
import { Table, Button, Input, Space } from 'antd'
import { ColumnProps } from 'antd/es/table'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { useConsoleUser } from '@/context/ConsoleUserContext'
import dayjs from 'dayjs'

type Props = {
  users: App.User[]
  getContractLabel: (contractStatus: App.User['contractStatus']) => string
  onPressShow: (id: App.User['id']) => void
  onPressDownload: (selectedRowKeys: (string | number)[]) => void
}

type SearchParams = {
  text: string
  column: string
}

export const Layout = (props: Props) => {
  const [searchParams, setSearchParams] = useState<SearchParams>({ text: '', column: '' })
  const [selectedRowKeys, setSelectedRowKeys] = useState<(string | number)[]>([])
  const [currentPageData, setCurrentPageData] = useState<App.User[]>([])
  const { admin } = useConsoleUser()

  const getColumnTitle = (dataIndex: string): string => {
    switch (dataIndex) {
      case 'customerNumber':
        return '顧客番号'
      case 'emailAddress':
        return 'メールアドレス'
      case 'nickName':
        return 'ニックネーム'
      case 'contractStatusUpdatedAt':
        return '契約更新日時'
      case 'createdAt':
        return '登録日時'
      default:
        return ''
    }
  }

  const getColumnSearchProps = (dataIndex: string): ColumnProps<App.User> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          // ref={node => {
          //   this.searchInput = node
          // }}
          placeholder={`${getColumnTitle(dataIndex)}を検索`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            リセット
          </Button>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}>
            検索
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) => {
      switch (dataIndex) {
        case 'customerNumber':
          return record.customerNumber
            ? record.customerNumber.toLowerCase().includes(value.toString().toLowerCase())
            : false
        case 'emailAddress':
          return record.emailAddress
            ? record.emailAddress.toLowerCase().includes(value.toString().toLowerCase())
            : false
        case 'nickName':
          return record.nickName ? record.nickName.includes(value.toString()) : false
        case 'createdAt':
          return record.createdAt
            ? record.createdAt
                .toDate()
                .toLocaleString('ja-JP')
                .includes(value.toString())
            : false
        case 'contractStatusUpdatedAt':
          return record.contractStatusUpdatedAt
            ? record.contractStatusUpdatedAt
                .toDate()
                .toLocaleString('ja-JP')
                .includes(value.toString())
            : false
            default:
          return false
      }
    },
    render: text =>
      searchParams.column === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchParams.text]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const handleSearch = (selectedKeys: any, confirm: any, dataIndex: string) => {
    confirm()
    setSearchParams({
      text: selectedKeys[0],
      column: dataIndex,
    })
  }

  const handleReset = (clearFilters: any) => {
    clearFilters()
    setSearchParams({
      text: '',
      column: '',
    })
  }

  const onSelectChange = (selectedRowKeys: (string | number)[]) => {
    setSelectedRowKeys(selectedRowKeys)
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: App.User) => ({
      disabled: currentPageData.length !== 0 && !currentPageData.includes(record),
      name: record.customerNumber,
    }),
  }

  return (
    <ContentBody>
      {admin && (
        <>
          <Link to='/users/bulkUpdate'>
            <StyledNewButton>契約状況を一括変更</StyledNewButton>
          </Link>
          <StyledNewButton
            disabled={selectedRowKeys.length === 0}
            onClick={() => props.onPressDownload(selectedRowKeys)}>
            ダウンロード
          </StyledNewButton>
        </>
      )}
      <Table
        rowKey="id"
        dataSource={props.users}
        rowSelection={rowSelection}
        onChange={(_patination, _filters, _sorter, extra) => {
          setCurrentPageData(extra.currentDataSource)
        }}
        columns={[
          {
            title: 'メールアドレス',
            dataIndex: 'emailAddress',
            key: 'emailAddress',
            ...getColumnSearchProps('emailAddress'),
          },
          {
            title: '顧客番号',
            dataIndex: 'customerNumber',
            key: 'customerNumber',
            ...getColumnSearchProps('customerNumber'),
          },
          {
            title: 'ニックネーム',
            dataIndex: 'nickName',
            key: 'nickName',
            ...getColumnSearchProps('nickName'),
          },
          {
            title: '契約状況',
            dataIndex: 'contractStatus',
            key: 'contractStatus',
            filters: [
              { text: '契約中', value: 'underContract' },
              { text: '解約済', value: 'canceled' },
              { text: '休止中', value: 'suspended' },
              { text: '解約予定', value: 'cancelScheduled' },
              { text: '休止予定', value: 'suspendScheduled' },
            ],
            onFilter: (value, record) => record.contractStatus === value,
            render: (contractStatus: App.User['contractStatus']) => (
              <div style={{ fontSize: 13, width: 130 }}>{props.getContractLabel(contractStatus)}</div>
            ),
          },
          {
            title: '契約変更日',
            dataIndex: 'contractStatusUpdatedAt',
            key: 'contractStatusUpdatedAt',
            sorter: (a, b) => {
              const d1 = a.contractStatusUpdatedAt ? moment(a.contractStatusUpdatedAt.toDate()).unix() : 0
              const d2 = b.contractStatusUpdatedAt ? moment(b.contractStatusUpdatedAt.toDate()).unix() : 0
              return d1 - d2
            },
            ...getColumnSearchProps('contractStatusUpdatedAt'),
            render: (contractStatusUpdatedAt: firebase.firestore.Timestamp) => (
              <div style={{ fontSize: 13, width: 130 }}>{contractStatusUpdatedAt ? dayjs(contractStatusUpdatedAt.toDate()).format('YYYY/MM/DD') : ''}</div>
            ),
          },
          {
            title: '登録日時',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: (a, b) => moment(a.createdAt.toDate()).unix() - moment(b.createdAt.toDate()).unix(),
            defaultSortOrder: 'descend',
            ...getColumnSearchProps('createdAt'),
            render: (cteatedAt: firebase.firestore.Timestamp) => (
              <div style={{ fontSize: 13, width: 130 }}>{cteatedAt.toDate().toLocaleString('ja-JP')}</div>
            ),
          },
          {
            title: '',
            key: 'action',
            render: (user: App.User) => {
              return (
                <>
                  <div style={{ display: 'flex' }}>
                    <StyledButton onClick={() => props.onPressShow(user.id)}>詳細</StyledButton>
                  </div>
                </>
              )
            },
          },
        ]}
      />
    </ContentBody>
  )
}

const ContentBody = styled.div`
  background: #fff;
  padding: 24px;
  min-height: 360px;
`

const StyledButton = styled(Button)`
  margin: 0 3px;
  font-size: 12px;
`

const StyledNewButton = styled(Button)`
  margin: 12px 3px;
  font-size: 12px;
`
